import { Component } from 'tosijs'
import * as BABYLON from '@babylonjs/core'
import type { B3d, FrameInfo } from './tosi-b3d.js'
import { sendDebug } from './debug-sink.js'
import { realNow } from './clock.js'

const DEG_TO_RAD = Math.PI / 180

export function findB3dOwner(el: HTMLElement): B3d | null {
  let node: HTMLElement | null = el.parentElement
  while (node != null) {
    if (
      'scene' in node &&
      'register' in node &&
      typeof (node as any).register === 'function'
    ) {
      return node as unknown as B3d
    }
    node = node.parentElement
  }
  return null
}

/**
 * The element's SEMANTIC parent — its nearest ancestor that isn't a tosijs slot
 * wrapper. tosijs mounts a component's light-DOM children inside a `<tosi-slot>`, so
 * a child's `parentElement` is that slot, not the component you nested it in. Any
 * child that wants to find "the thing I'm nested in" (a radar in an aircraft, a
 * radar-blip in a target) must skip the slot(s).
 */
export function semanticParent(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement
  while (node != null && node.tagName === 'TOSI-SLOT') {
    node = node.parentElement
  }
  return node
}

/**
 * WHERE THE PLAYER'S HANDS ARE — the point an interaction's `reach` is measured from.
 *
 * Not the camera. A picking ray starts at the eye, and flat, the eye and the
 * hand are the same point — so `pickInfo.distance` is a perfectly good reach
 * everywhere EXCEPT behind a third-person character, where the camera trails
 * several metres and every human-sized reach is exceeded while you stand with
 * your nose against the switch. CLAUDE.md names this as the one thing that does
 * not transfer between surfaces; it says it of a headset, and a chase camera is
 * the same geometry for the same reason.
 *
 * Walks the scene's own subtree for the live `player: true` controllable — an
 * interactive is a sibling of the player, not a descendant, so there is no
 * parent chain to climb. Returns `null` when there is no player (an orbit-camera
 * scene, a spectator), which means "measure along the ray", the old behaviour.
 */
export function playerPosition(owner: B3d | null): BABYLON.Vector3 | null {
  const host = owner as unknown as HTMLElement | null
  if (host == null || typeof host.querySelectorAll !== 'function') return null
  for (const node of host.querySelectorAll('*')) {
    const e = node as any
    if (
      e.player === true &&
      !e.dead &&
      !e.crashed &&
      e.mesh?.position != null
    ) {
      return e.mesh.position as BABYLON.Vector3
    }
  }
  return null
}

/**
 * Rebuild a container's children WITHOUT destroying the popup layers in it.
 *
 * A `widgets3d` popup mounts as a SIBLING of the panel it belongs to — that is
 * what a DOM layer is for, since a popup inside the panel's `<svg>` is cropped
 * by its viewBox — so it is a child of whatever holds the panel. Which makes a
 * plain `replaceChildren` on that container a popup killer, and an invisible
 * one: the popup does not error, it simply stops existing.
 *
 * That is what "clicking reset worst closed the panel" was. Every action button
 * in a debug panel repaints, every repaint rebuilt the host, and the popup the
 * button was IN went with it. Re-appending the holders keeps everything a
 * rebuild has no business touching: where the popup was dragged to, how far it
 * was scrolled, and the live rows already ticking inside it.
 *
 * Named and tested rather than inlined because it went wrong twice — the second
 * time as a commit message describing a fix the diff did not contain.
 */
export function replaceKeepingLayers(
  host: Element,
  ...children: Array<Node | string>
): void {
  const keep = [...host.querySelectorAll(':scope > [data-w3d-dom-layer]')]
  host.replaceChildren(...children, ...keep)
}

export function actualMeshes(meshes: BABYLON.AbstractMesh[]): BABYLON.Mesh[] {
  return meshes.filter(
    (mesh) => (mesh as BABYLON.Mesh).geometry != null
  ) as BABYLON.Mesh[]
}

/**
 * Is an on-by-default toggle in its OFF state? Use for feature flags that should
 * default ON: declare them as a string `'on' | 'off'` attribute defaulting `'on'`
 * (a boolean attribute can't default true — an absent boolean reads false; see the
 * b3d-trigger `disabled` note). `isOff` also accepts the boolean `false` / string
 * `'false'` a UI toggle may bind, so a `toggle3d` still disables it.
 */
export const isOff = (v: unknown): boolean =>
  v === 'off' || v === false || v === 'false'

/**
 * A node name with the `.model` EXPORT marker removed — what every naming-
 * convention suffix check (`_collideMesh`, `_noshadow`, `_mirror`, `-ignore`,
 * …) runs against. `.model` declares a library export (see b3d-library) and
 * is ORTHOGONAL to behaviour suffixes: `Hull_collideMesh.model` both exports
 * AND gets its collider. Stripping it here makes that a stated guarantee
 * rather than an accident of `includes()`-based matching (clone names like
 * `scout.model_instance_0` are covered too).
 */
/**
 * THE frame delta for anything ticking inside a scene observer (seconds).
 *
 * Babylon's `engine.getDeltaTime()` measures the engine's rAF TICK, but scene
 * observers only fire when `scene.render()` runs — and `<tosi-b3d>` throttles
 * rendering to its `frameRate`. Trusting getDeltaTime there advances too
 * little time per frame: at frameRate 60 on a 120Hz display everything runs
 * at HALF speed, at the default 30 a QUARTER. B3d publishes the real
 * inter-render delta on `scene.metadata.b3dFrameDelta`; this reads it, with
 * the raw engine delta as a fallback for scenes B3d doesn't drive (tests,
 * standalone Babylon).
 */
export const sceneDelta = (scene: BABYLON.Scene): number => {
  const published = (scene.metadata as any)?.b3dFrameDelta
  // `>= 0`, not `> 0`: a published ZERO is <tosi-b3d> saying TIME IS STOPPED,
  // and it has to be honoured or every simulation on the render observable keeps
  // advancing through a pause. The engine-delta fallback is for scenes that
  // never publish at all, not for a scene that published a deliberate zero.
  // (tosijs-3d#30 — paused, the world ran on at full speed and only the stick
  // stopped working: 66m of travel over a 3-second pause.)
  if (typeof published === 'number' && published >= 0) return published
  return Math.min(0.1, (scene.getEngine().getDeltaTime() || 16) / 1000)
}

/**
 * The whole per-frame package for a scene, for code with no `owner` to ask.
 *
 * `sceneDelta` answers "how much time passed" and every caller then has to
 * work out the rest for itself — which clock, is it paused, how long has this
 * been going. This hands over all of it, so the right choice is the one
 * already in your hand.
 *
 * Falls back to a sane package for scenes `<tosi-b3d>` does not drive (tests,
 * standalone Babylon), where `dt` and `realDt` are simply the same number.
 */
/**
 * The clock for a shader's animation: the scene's SIM seconds, so weather and
 * waves stop when the world is paused and slow with `timeScale`. Wall-clock
 * seconds in a scene `<tosi-b3d>` does not drive.
 */
export const sceneSeconds = (scene: BABYLON.Scene): number => {
  const published = (scene.metadata as { b3dFrame?: FrameInfo } | null)
    ?.b3dFrame
  return published != null ? published.elapsed : realNow() * 0.001
}

export const sceneFrame = (scene: BABYLON.Scene): FrameInfo => {
  const published = (scene.metadata as { b3dFrame?: FrameInfo } | null)
    ?.b3dFrame
  if (published != null) return published
  const dt = sceneDelta(scene)
  return {
    dt,
    realDt: dt,
    elapsed: 0,
    realElapsed: 0,
    scale: 1,
    paused: dt === 0,
    frame: 0,
  }
}

export const conventionName = (name: string): string =>
  name.split('.model').join('')

/**
 * The BEHAVIOUR suffixes, longest-first so `_collideMesh` is matched before
 * `_collide`. Lower-case; matching is case-insensitive.
 */
const BEHAVIOUR_SUFFIXES = [
  // hyphen forms too: the framework documents `-ignore`, and authors mix
  // separators (the collision code already matches both).
  'centerofgravity',
  'center_of_gravity',
  'collidemesh',
  'collide_mesh',
  'collidesphere',
  'collide_sphere',
  'collidecylinder',
  'collide_cylinder',
  'collidebox',
  'collide_box',
  'collide',
  'noshadow',
  'nocast',
  'mirror',
  'ignore',
]

/**
 * The name a CONSUMER uses: `conventionName` (drop `.model`) with the
 * behaviour suffixes stripped too, so `Hull_collideMesh.model` is publicly
 * `Hull`.
 *
 * The suffixes are annotations telling the engine what to DO with a node —
 * collider shape, shadow participation, the CoG marker — not part of what the
 * thing IS. Leaking them into `getNames()` makes a consumer type engine
 * plumbing they never chose (`instantiate('Hull_collideMesh')`), and it breaks
 * the moment an author adds or changes a collider. Repeats, so a node carrying
 * two annotations (`Hull_collideMesh_noshadow`) still resolves to `Hull`.
 */
/**
 * Is this node FILTERED OUT at load? The convention is written `-ignore` in the
 * docs and `_ignore` by the underscore rule every other suffix follows, so both
 * are accepted — one matcher, used by the loader, the library and `publicName`.
 *
 * They used to disagree: the loader disposed only the hyphen form while
 * `publicName` stripped only the underscore one, so `Foo_ignore` survived the
 * load AND collapsed to `Foo` — colliding with a real `Foo`, which made
 * `instantiate('Foo')` resolve to whichever came first.
 */
export const isIgnored = (name: string): boolean => {
  const n = conventionName(name).toLowerCase()
  return n.includes('-ignore') || n.includes('_ignore')
}

export const publicName = (name: string): string => {
  /*
  `_primitive0` is not authored — it is the glTF loader SPLITTING one mesh that
  uses several materials into one Babylon mesh per material. It is an artifact
  of how the file was imported, and a consumer has no way to know it exists, so
  it has no business in a name they have to type or read. Stripped FIRST,
  because it sits outside the behaviour suffix: `building_collideCylinder_primitive0`
  only cleans to `building` if the primitive tag comes off before the loop.

  Tonio, looking at the library picker: "should show names like building, not
  building_collideCylinder".
  */
  let n = conventionName(name).replace(/_primitive\d+$/i, '')
  /*
  Blender's DUPLICATE marker (`.001`) lands after the behaviour suffix, so
  `tree_collideCylinder.001` matched no suffix and leaked the annotation whole.
  Held aside and put back rather than dropped: `.001` is a genuinely different
  object from `tree`, and collapsing the two would silently merge them in a name
  list that dedupes.
  */
  const dup = n.match(/\.\d+$/)
  if (dup != null) n = n.slice(0, -dup[0].length)
  for (;;) {
    const lower = n.toLowerCase()
    const hit = BEHAVIOUR_SUFFIXES.find((s) => lower.endsWith(`_${s}`))
    if (hit == null) return dup != null ? n + dup[0] : n
    n = n.slice(0, -(hit.length + 1))
  }
}

/**
 * World-space Y of the bottom of a node's combined geometry (the node itself
 * plus every descendant mesh). Returns null if there's no renderable geometry.
 */
function hierarchyMinWorldY(node: BABYLON.TransformNode): number | null {
  node.computeWorldMatrix(true)
  const meshes = [
    ...(node instanceof BABYLON.AbstractMesh ? [node] : []),
    ...node.getChildMeshes(false),
  ].filter((m) => m.getTotalVertices() > 0)
  if (meshes.length === 0) return null
  let minY = Infinity
  for (const mesh of meshes) {
    mesh.computeWorldMatrix(true)
    const y = mesh.getBoundingInfo().boundingBox.minimumWorld.y
    if (y < minY) minY = y
  }
  return minY
}

/** What a controllable needs to know from its owner to decide whether to run. */
export interface SimGateOwner {
  paused?: boolean
  frozen?: boolean
  inputSuppressed?: boolean
  hasInputFocus?: boolean
}

/**
 * Is the SIMULATION stopped for this owner? Then a controllable must **halt**,
 * not merely read empty input.
 *
 * The distinction has cost us twice. A controllable's clock is `Date.now`-based,
 * so a stopped scene does not slow it down, and an aircraft fed empty input
 * COASTS — indistinguishable from cruising ("I can background the tab, come back
 * and the game is continuing to run, I just can't steer", #30). `freeze()` then
 * repeated the mistake in miniature: it stopped everything on `sceneDelta`
 * (terrain, water, projectiles) while the piloted aircraft flew on, so raising
 * the re-seat prompt in flight could fly you into the ground while the modal was
 * up.
 *
 * Pure and exported so BOTH gates live in one place and a third state added
 * later lands in both. This is the predicate `B3dControllable._update` halts on.
 */
export function simHalted(owner: SimGateOwner | null | undefined): boolean {
  return owner?.paused === true || owner?.frozen === true
}

/**
 * Should this controllable READ its input this frame? False also when input is
 * modally suppressed (a dialog borrowing a control you also play with) or when
 * another demo on the page holds scene focus.
 *
 * Distinct from `simHalted`: halted means "do not advance at all", not-live
 * means "advance, but with neutral input" — which is right for an unfocused
 * demo that should idle rather than freeze.
 */
export function controlsLive(owner: SimGateOwner | null | undefined): boolean {
  if (simHalted(owner)) return false
  if (owner?.inputSuppressed === true) return false
  return owner?.hasInputFocus ?? true
}

/**
 * Mark a mesh as **UI**: pickable by pointers, invisible to COLLISION.
 *
 * These are different questions and we had only one answer for both. A spatial
 * panel MUST stay `isPickable` — that is how a controller ray, a gaze cursor or
 * a mouse targets it — and `isPickable` was also the only thing collision
 * predicates could filter on. So the aircraft's impact sweep, which crashes on
 * ANY hit above `crashSpeed`, treated the settings panel floating in front of
 * your face as terrain.
 *
 * That is the phantom collision (Tonio, VR pass 2 — `crashReport` named it:
 * `hit=frame-panel`). It only bit in cockpit view because that is where the
 * camera — and the panels riding its reference frames — sit close enough to the
 * airframe to fall inside a ~2m sweep, and it fired on BANK because banking
 * swings the velocity vector into the panel. Nothing to do with terrain, which
 * is why it reproduced in scenes that have none.
 *
 * The first of the collision GROUPS described in `COLLISION-DESIGN.md`. Written
 * on metadata rather than a name suffix because UI planes are built by us, not
 * authored in Blender.
 */
export function markUiMesh(mesh: BABYLON.AbstractMesh): void {
  mesh.metadata = { ...(mesh.metadata ?? {}), b3dNoCollide: true }
}

/**
 * Should a collision probe ignore this mesh? True for UI (see `markUiMesh`).
 *
 * Prefer `collidable()` over calling this directly — a rule every call site has
 * to remember is a rule that gets forgotten, which is exactly what happened.
 */
export function isNoCollide(mesh: BABYLON.AbstractMesh): boolean {
  return (
    (mesh.metadata as { b3dNoCollide?: boolean } | null)?.b3dNoCollide === true
  )
}

/**
 * Whether `node` is part of what is CARRYING `rig`: beneath the rig's parent
 * (the vehicle you ride) without being beneath the rig itself (your own
 * panels). False when the rig has no parent. Structural, so it is testable
 * with plain `{ parent }` objects.
 */
export function insideCarrier(
  node: { parent?: unknown } | null,
  rig: { parent?: unknown } | null
): boolean {
  const carrier = rig?.parent
  if (carrier == null) return false
  let n: unknown = node
  while (n != null) {
    if (n === rig) return false
    if (n === carrier) return true
    n = (n as { parent?: unknown }).parent
  }
  return false
}

/**
 * THE collision predicate. Everything physical picks through this.
 *
 * Excludes UI **by default** — you opt IN to hitting it, never out. That
 * polarity is the whole point: `isNoCollide` shipped as an opt-out clause each
 * predicate had to remember, and within one release two of the four sites had
 * forgotten it, so a world-anchored panel stopped shells and acted as blast
 * cover. See `COLLISION-DESIGN.md` → "Default groups".
 *
 * It also re-checks `isPickable`/`isEnabled` centrally, because **passing a
 * predicate to `pickWithRay` makes Babylon skip its own filter** — the trap that
 * once had an aircraft pick a cloud blob as ground. Note that `isPickable` is
 * not the same as *visible*: a gaze-revealed panel hides with `visibility = 0`
 * and stays pickable, so without the UI exclusion an unrevealed panel is an
 * invisible bullet shield.
 *
 * `reject` is for the caller's OWN business — self-exclusion, `__root__`, water
 * for a submersible — never for the shared rules above.
 */
export function collidable(
  reject?: (m: BABYLON.AbstractMesh) => boolean,
  opts: { ignoreGroups?: readonly string[] } = {}
): (m: BABYLON.AbstractMesh) => boolean {
  const ignore = opts.ignoreGroups
  return (m: BABYLON.AbstractMesh): boolean =>
    m.isPickable &&
    m.isEnabled() &&
    !isNoCollide(m) &&
    !(ignore != null && inCollisionGroup(m, ignore)) &&
    !(reject?.(m) ?? false)
}

/**
 * A RAY PROXY answers rays for meshes it owns, faster than Babylon can: a
 * city knows its ground is flat and its buildings are boxes in a grid, where
 * a scene pick walks every thin-instanced copy of every model (thousands, a
 * matrix multiply each, for every wheel's ray every physics step: most of a
 * Quest's frame). Meshes it answers for carry `metadata.b3dRayProxy = true`;
 * `pickWorld` skips them in the scene pick and asks the proxies instead.
 */
export interface RayProxy {
  /** World-space ray (unit `dir`): how far along it, and the surface's normal, or null. */
  cast(
    origin: BABYLON.Vector3,
    dir: BABYLON.Vector3,
    length: number
  ): { distance: number; normal: BABYLON.Vector3 } | null
}

const RAY_PROXIES = new WeakMap<BABYLON.Scene, Set<RayProxy>>()

/** The scene's ray proxies (add yours; remove it when you go). */
export function rayProxies(scene: BABYLON.Scene): Set<RayProxy> {
  let set = RAY_PROXIES.get(scene)
  if (set == null) {
    set = new Set()
    RAY_PROXIES.set(scene, set)
  }
  return set
}

const proxied = (m: BABYLON.AbstractMesh) =>
  (m.metadata as { b3dRayProxy?: boolean } | null)?.b3dRayProxy === true

/**
 * A pick against the world: the scene's meshes (by `predicate`, as
 * `collidable` gives) except those a ray proxy answers for, and the proxies.
 * The nearest hit, with its normal (facing the ray), or null.
 */
export function pickWorld(
  scene: BABYLON.Scene,
  ray: BABYLON.Ray,
  predicate: (m: BABYLON.AbstractMesh) => boolean
): {
  distance: number
  normal: BABYLON.Vector3
  mesh: BABYLON.AbstractMesh | null
} | null {
  const proxies = RAY_PROXIES.get(scene)
  const hit = scene.pickWithRay(
    ray,
    proxies?.size ? (m) => predicate(m) && !proxied(m) : predicate
  )
  let best: {
    distance: number
    normal: BABYLON.Vector3
    mesh: BABYLON.AbstractMesh | null
  } | null = null
  if (hit?.hit && hit.pickedMesh) {
    let n = hit.getNormal(true, true) ?? ray.direction.negate()
    if (BABYLON.Vector3.Dot(n, ray.direction) > 0) n = n.negate()
    best = { distance: hit.distance, normal: n, mesh: hit.pickedMesh }
  }
  if (proxies)
    for (const p of proxies) {
      const h = p.cast(
        ray.origin,
        ray.direction,
        best ? best.distance : ray.length
      )
      if (h && (best == null || h.distance < best.distance))
        best = { ...h, mesh: null }
    }
  return best
}

/**
 * Tag a mesh as belonging to one or more collision GROUPS.
 *
 * The single `b3dNoCollide` boolean answers "does anything hit this", which is
 * one answer for every asker — and a sea needs two. tosijs-3d-ensemble put it
 * exactly: *"the aircraft should not treat water as ground" and "shells should
 * splash on water" are the same switch*, so a flying submarine had to choose
 * between crashing on the surface and having ordnance that passes through it
 * invisibly (#44). Their workaround was clearing `isPickable` on the sea, which
 * buys the first and loses the second for every consumer of the predicate.
 *
 * Groups move the answer to the ASKER. The mesh says what it is; each mover
 * says what it treats as solid:
 *
 * ```js
 * markCollisionGroup(waterMesh, 'water')
 *
 * // an aircraft that must not land on the sea
 * scene.pickWithRay(ray, collidable(skip, { ignoreGroups: ['water'] }))
 * // a shell that must splash on it — unchanged, so it still hits
 * scene.pickWithRay(ray, collidable(skip))
 * ```
 *
 * Additive, and it has to be: a mesh can be several things at once (a hull is
 * `vehicle` and, to a torpedo, a `target`), and a scene with a seaplane and a
 * submarine in it needs one sea to give two answers on the same frame. No
 * per-element attribute can do that, which is why this is per-QUERY.
 */
export function markCollisionGroup(
  mesh: BABYLON.AbstractMesh,
  ...groups: string[]
): void {
  const meta = (mesh.metadata ?? {}) as { b3dGroups?: string[] }
  const have = new Set(meta.b3dGroups ?? [])
  for (const g of groups) if (g !== '') have.add(g)
  mesh.metadata = { ...meta, b3dGroups: [...have] }
}

/** The groups a mesh has been tagged with. Empty when it has none. */
export function collisionGroups(mesh: BABYLON.AbstractMesh): readonly string[] {
  return (
    (mesh.metadata as { b3dGroups?: string[] } | null)?.b3dGroups ??
    EMPTY_GROUPS
  )
}

const EMPTY_GROUPS: readonly string[] = []

/** Is this mesh in ANY of `groups`? */
export function inCollisionGroup(
  mesh: BABYLON.AbstractMesh,
  groups: readonly string[]
): boolean {
  const mine = collisionGroups(mesh)
  if (mine.length === 0) return false
  for (const g of groups) if (mine.includes(g)) return true
  return false
}

/**
 * Vertical gap, in world units, between a node's origin and the bottom of its
 * geometry. Handy as a ground clearance so a model rests on a surface instead
 * of its origin sinking into it (origins are rarely at the model's feet).
 */
/**
 * World-space extents of a node's whole hierarchy, or `null` if it has no
 * geometry.
 *
 * `skip` drops descendants by name — which is not a nicety. A vehicle's
 * hierarchy carries things that are not the vehicle: an aiming reticle parented
 * to the airframe sits at GUN RANGE, so including it measured the scout as 115 m
 * long instead of 4.9 and would have derived every camera offset from a ring
 * floating a hundred metres ahead of the nose.
 *
 * `keep` is the other half: measure only what matches, which is how a named
 * sub-assembly (a `Cockpit` node) gets measured on its own.
 */
export function hierarchyExtents(
  node: BABYLON.TransformNode,
  options: { skip?: RegExp; keep?: RegExp } = {}
): { min: BABYLON.Vector3; max: BABYLON.Vector3 } | null {
  let min: BABYLON.Vector3 | null = null
  let max: BABYLON.Vector3 | null = null
  for (const mesh of node.getChildMeshes(false)) {
    if (options.skip?.test(mesh.name)) continue
    if (options.keep != null && !options.keep.test(mesh.name)) continue
    if (mesh.getTotalVertices() === 0) continue
    mesh.computeWorldMatrix(true)
    const box = mesh.getBoundingInfo().boundingBox
    if (min == null || max == null) {
      min = box.minimumWorld.clone()
      max = box.maximumWorld.clone()
    } else {
      min.minimizeInPlace(box.minimumWorld)
      max.maximizeInPlace(box.maximumWorld)
    }
  }
  return min != null && max != null ? { min, max } : null
}

export function boundingBottomOffset(node: BABYLON.TransformNode): number {
  const minY = hierarchyMinWorldY(node)
  if (minY == null) return 0
  return node.getAbsolutePosition().y - minY
}

/**
 * Place `node` so the bottom of its geometry rests on a surface, leaving a
 * small `separation` gap. `surface` is either a world Y height (default 0, the
 * ground plane) or a mesh to sit on top of (uses the top of its bounding box).
 * Works regardless of where the model's origin sits within its mesh.
 */
export function placeOnSurface(
  node: BABYLON.TransformNode,
  surface: number | BABYLON.AbstractMesh = 0,
  separation = 0.02
): void {
  const surfaceY =
    typeof surface === 'number'
      ? surface
      : surface.getBoundingInfo().boundingBox.maximumWorld.y
  const minY = hierarchyMinWorldY(node)
  if (minY == null) return
  node.position.y += surfaceY + separation - minY
}

export type AsyncVoidFunction = () => Promise<void>

export type XRParams = {
  cameraName?: string
  mode?: XRSessionMode
}

export type XRStuff = {
  camera: BABYLON.FreeCamera
  xr: BABYLON.WebXRDefaultExperience
  exitXR: AsyncVoidFunction
}

export async function enterXR(
  scene: BABYLON.Scene,
  options: XRParams = {}
): Promise<XRStuff> {
  const { cameraName = 'xr-camera', mode = 'immersive-vr' } = options
  if (navigator.xr == null) {
    throw new Error('xr is not available')
  }
  if (!(await navigator.xr.isSessionSupported(mode))) {
    throw new Error(`navigator.xr does not support requested mode "${mode}"`)
  }
  const xr = await scene.createDefaultXRExperienceAsync({
    uiOptions: { sessionMode: mode },
    // The right hand points first (see tosi-b3d: otherwise a first press is lost).
    pointerSelectionOptions: { preferredHandedness: 'right' } as any,
  })
  const { baseExperience } = xr
  const { camera } = baseExperience
  camera.name = cameraName
  await baseExperience.enterXRAsync(mode, 'local-floor')
  return {
    camera,
    xr,
    async exitXR() {
      await baseExperience.exitXRAsync()
    },
  }
}

/*#
# Material Conventions

These conventions apply to all meshes entering the scene — via
[b3d-loader](?b3d-loader.ts), [b3d-library](?b3d-library.ts), or any
component that calls `register()`.

## Example — behaviour from names

Material *appearance* (metallic, roughness, alpha, emissive) comes through glTF automatically.
*Behaviour* that can't be inferred from a material is driven by **name suffixes** on the mesh
(set in Blender, by a loader, or in code):

```javascript
// glass_mirror        → gets a dynamic reflection probe
// floor_noshadow      → does not receive shadows
// lamp_nocast         → does not cast shadows
// crate_collideBox    → box collider (also _collide / _collideSphere / _collideCylinder / _collideMesh)
// debug-ignore        → disposed on load (filtered out)
```

## Property-Based (automatic from Blender materials)

| Property | Threshold | Effect |
|----------|-----------|--------|
| `alpha` > 0.95 | snapped to 1.0 | Treated as fully opaque (avoids blend cost) |
| `alpha` ≤ 0.95 | — | Alpha blend, depth pre-pass, double-sided, excluded from shadow casting |
| `unlit` (glTF KHR_materials_unlit) | — | Respected as-is |
| Transmission > 0 + `_mirror` | — | Cubemap-based refraction with proper IOR (replaces glTF screen-space) |

## Name Suffixes (behavioral overrides, not material appearance)

| Suffix | Effect |
|--------|--------|
| `_noshadow` / `-noshadow` | Mesh doesn't receive shadows |
| `_nocast` / `-nocast` | Mesh doesn't cast shadows |
| `_mirror` / `-mirror` | Dynamic reflection probe (+ refraction if transmissive) |
| `-ignore` | Node is disposed on load |
| `_collide*` | Physics collider (sphere/box/cylinder/mesh) |
*/
/*{ "parent": "core", "order": 70 }*/

// Thresholds for property-based material inference
const ALPHA_OPAQUE_THRESHOLD = 0.95

/**
 * Dispose a mesh AND the materials/textures nothing else is still using.
 *
 * `mesh.dispose()` leaves its material behind — Babylon's
 * `disposeMaterialAndTextures` defaults to false — so a child that builds its
 * own material leaks it on every teardown. That includes every RE-PARENT,
 * because a move disconnects and reconnects the child while `B3d` deliberately
 * keeps the scene alive: the mesh goes, a fresh material is built, and the old
 * one stays in `scene.materials` for the life of the page.
 *
 * Measured, not theorised. Re-parenting one `<tosi-b3d>` holding a skybox and
 * water six times, with the scene surviving each move as designed:
 *
 * ```
 *   meshes     2   2   2   2   2   2   2      correctly disposed
 *   materials  3   5   7   9  11  13  15      +2 every move
 *   textures   3   6   9  12  15  18  21      +3 every move
 * ```
 *
 * That is also the diagnostic `tosijs-3d-ensemble` reported against #56 —
 * `gl.isProgram(program) === false` while every uniform reads correct — and the
 * attribution matters: it is an ORPHANED material holding a dead program, not
 * the live one, so that signature is not by itself evidence of a second WebGL
 * context.
 *
 * ## Why it checks rather than just disposing
 *
 * A glTF file routinely shares one material across many meshes, so disposing a
 * material because THIS mesh referenced it would leave its siblings black —
 * silently, since a disposed material still answers `isReady()`. So a material
 * goes only when no mesh left in the scene refers to it, and a texture only
 * when no MESH LEFT IN THE SCENE refers to it through its material.
 *
 * ⚠️ Both guards ask `scene.meshes`, and the texture one has to. A
 * library-instantiated model's materials are **not in `scene.materials`** —
 * they belong to the `AssetContainer` — while its meshes ARE in `scene.meshes`
 * (measured on `/b3d-prop/` with real Kenney GLBs). So a texture guard written
 * against `scene.materials` cannot see a library material, and would dispose a
 * texture shared between a hand-made material and a library one while the
 * library model was still drawing it. The mesh list is the only list that sees
 * everything.
 *
 * And a material the scene does NOT list (`scene.materials`) is never
 * disposed: it belongs to an AssetContainer, whose source meshes still use it
 * (#102: the last instance of a library model going took the model's colour
 * with it).
 *
 * Textures get three extra exemptions, each of which is a thing that holds a
 * texture without any material mentioning it: the scene's environment texture,
 * anything in `customRenderTargets` (water's reflection/refraction), and a
 * light's `projectionTexture` (a lamp's gel).
 */
export function disposeMeshTree(mesh: BABYLON.AbstractMesh): void {
  const scene = mesh.getScene()
  const materials = new Set<BABYLON.Material>()
  const collect = (m: BABYLON.AbstractMesh): void => {
    if (m.material != null) materials.add(m.material)
    const sub = (m.material as BABYLON.MultiMaterial | null)?.subMaterials
    if (sub != null) for (const s of sub) if (s != null) materials.add(s)
  }
  collect(mesh)
  for (const child of mesh.getChildMeshes()) collect(child)

  mesh.dispose()

  const usesMaterial = (m: BABYLON.AbstractMesh, mat: BABYLON.Material) =>
    m.material === mat ||
    ((m.material as BABYLON.MultiMaterial | null)?.subMaterials?.includes(
      mat as BABYLON.Material
    ) ??
      false)

  /*
  Textures still in use, derived from the materials of the meshes that REMAIN.
  Computed once and lazily: `mesh.dispose()` has already run, so `scene.meshes`
  is the final list and nothing below adds to it. A material we are about to
  dispose is by construction on no remaining mesh, so it contributes nothing
  here and cannot vote to keep its own textures alive.
  */
  let liveTextures: Set<BABYLON.BaseTexture> | null = null
  const stillDrawn = (tex: BABYLON.BaseTexture): boolean => {
    if (liveTextures == null) {
      liveTextures = new Set()
      for (const m of scene.meshes) {
        const mats: BABYLON.Material[] = []
        if (m.material != null) mats.push(m.material)
        const sub = (m.material as BABYLON.MultiMaterial | null)?.subMaterials
        if (sub != null) for (const s of sub) if (s != null) mats.push(s)
        for (const mm of mats) {
          try {
            for (const t of mm.getActiveTextures())
              if (t != null) liveTextures.add(t)
          } catch {
            // Same reason as below: a half-built material must not fail the
            // whole disposal. Its textures simply aren't counted as live.
          }
        }
      }
    }
    return liveTextures.has(tex)
  }

  /*
  ONLY WHAT THE SCENE OWNS (#102). A library's materials live in its
  AssetContainer and are not in `scene.materials`, and neither are the source
  meshes that still use them, so the guard above cannot see that the library
  needs them. Removing the last placed instance of a model disposed its
  materials, Babylon detached them from the source meshes too, and every
  later `instantiate()` came out white. A material the scene does not list is
  someone else's to dispose.
  */
  const owned = new Set(scene.materials)
  for (const mat of materials) {
    if (!owned.has(mat)) continue
    if (scene.meshes.some((m) => usesMaterial(m, mat))) continue
    // Read the textures BEFORE disposing, or there is nothing left to ask.
    let textures: BABYLON.BaseTexture[] = []
    try {
      textures = mat.getActiveTextures()
    } catch {
      // A half-built material can throw here; losing its textures to the
      // scene teardown is better than failing the whole disposal.
    }
    mat.dispose()
    for (const tex of textures) {
      if (tex == null) continue
      if (scene.environmentTexture === tex) continue
      if (scene.customRenderTargets.includes(tex as never)) continue
      if (
        scene.lights.some(
          (l) =>
            (l as unknown as { projectionTexture?: unknown })
              .projectionTexture === tex
        )
      ) {
        continue
      }
      if (stillDrawn(tex)) continue
      tex.dispose()
    }
  }
}

/**
 * Apply material conventions based on PBR material properties.
 *
 * Reads actual material data (alpha, metallic, etc.) rather than relying
 * on name suffixes for appearance. Near-opaque alpha is snapped to 1.0
 * to avoid unnecessary blend cost. Translucent materials get depth
 * pre-pass and shadow exclusion automatically.
 */
export function applyMaterialConventions(meshes: BABYLON.AbstractMesh[]): void {
  for (const mesh of meshes) {
    const mat = mesh.material
    if (mat == null) continue
    // Tag for shadow exclusion (read by b3d-shadows / dynamic-shadows)
    if (materialConventions(mat) && !mesh.name.includes('_transparent')) {
      mesh.name += '_transparent'
    }
  }
}

/**
 * The same rules on one material: near-opaque alpha snapped to 1, a
 * translucent one sorted with a depth pre-pass. True when it is translucent.
 */
export function materialConventions(mat: BABYLON.Material): boolean {
  // Snap near-opaque alpha to 1.0 — avoids blend pipeline for no visual benefit
  if (mat.alpha > ALPHA_OPAQUE_THRESHOLD && mat.alpha < 1) mat.alpha = 1
  // Translucent materials: correct sorting + perf optimizations
  // backFaceCulling is left as-is — driven by glTF doubleSided / Blender's setting
  if (mat.alpha > ALPHA_OPAQUE_THRESHOLD) return false
  mat.needDepthPrePass = true
  mat.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND
  return true
}

/*#
# B3dChild

`B3dChild` is the base class for **any custom element you write that lives inside a
`<tosi-b3d>` scene**. It owns the whole scene-attach lifecycle in one place so you
never have to reason about component-vs-scene timing yourself.

Extend it (or [AbstractMesh](?b3d-utils.ts), which extends `B3dChild` and adds
position/rotation syncing) and override two hooks:

| Hook | When it runs | What to do |
|------|--------------|------------|
| `sceneReady(owner, scene)` | Once, when **both** this element is connected (its attributes are drained and readable) **and** the scene's engine/scene exist. | Build your Babylon content, `owner.register({meshes, lights})`, subscribe to `owner.addSceneListener(...)`. |
| `sceneDispose()` | On disconnect (element removed, or the whole scene torn down). | Dispose meshes/materials, unsubscribe, release references. |

Do **not** override `connectedCallback` / `disconnectedCallback` — the pull-model
plumbing is centralized in `B3dChild` so a lifecycle fix lands in exactly one spot.
(If you must, call `super` first and keep it side-effect-free.)

## Moved is not removed

Re-parenting an element fires `disconnectedCallback` then `connectedCallback` in
the **same task**, and it need not touch your element at all — moving any
*ancestor* disconnects and reconnects everything beneath it. So the scene treats
a same-task reconnect as a move and keeps the engine; anything else is a genuine
removal and the engine is disposed outright. There is no pooling and nothing
kept warm: **disconnected means gone.**

That makes the two owner-level hooks trustworthy enough to build on, under one
rule — **subscriptions are durable, scene state is not**:

| Call | Guarantee |
|------|-----------|
| `owner.whenReady(cb)` | Runs now if the scene is up, else the instant it is. A callback still waiting when a scene is torn down is **not dropped** — it fires against the next scene, or never runs at all because the element never came back. |
| `owner.whenDisposed(cb)` | Runs when the scene is genuinely disposed, **not** on a move. Returns an unsubscribe. Survives a rebuild, so register once. |

Reach for `whenDisposed` for anything holding a reference *into* the scene
that `sceneDispose()` does not already cover — a timer closing over a mesh, a
cache of materials, an observer on a node. After disposal those point at a dead
scene, and Babylon's failure there is a black material that still reports
`isReady()`: silent, not loud.

Why a base class rather than parent orchestration? On connect the child discovers its
owner via [findB3dOwner](?b3d-utils.ts) and asks to attach: `owner.whenReady(cb)` runs
`cb` immediately if the scene is already up, otherwise the instant it becomes ready.
The parent never *pushes* `sceneReady` at a guessed moment, so `sceneReady` fires only
when the child is genuinely ready AND the scene exists — no races against tosijs's
deferred attribute application.

```javascript
import { B3dChild } from 'tosijs-3d'
import * as BABYLON from '@babylonjs/core'

class MyThing extends B3dChild {
  static preferredTagName = 'my-thing'
  static initAttributes = { color: '#ff8800' }
  declare color: string
  mesh?: BABYLON.Mesh

  sceneReady(owner, scene) {
    this.mesh = BABYLON.MeshBuilder.CreateBox('mything', {}, scene)
    owner.register({ meshes: [this.mesh] })
  }

  sceneDispose() {
    this.mesh?.dispose()
    this.mesh = undefined
  }
}
export const myThing = MyThing.elementCreator()
```
*/
/*{ "parent": "core", "order": 70 }*/

/**
 * Base for every element that lives INSIDE a `<tosi-b3d>` scene. The whole
 * pull-model lifecycle lives here, in ONE place. On connect (by which point tosijs
 * has drained this element's attributes), the child finds its b3d owner and asks to
 * insert itself once the scene is ready: `owner.whenReady(cb)` runs `cb` now if the
 * scene is already up, else when it becomes ready. b3d never *pushes* `sceneReady`
 * at a guessed time — so a child's `sceneReady` only ever runs when the child is
 * genuinely ready AND the scene is ready. On disconnect it removes itself.
 *
 * Subclasses override `sceneReady(owner, scene)` (build/insert into the scene) and
 * `sceneDispose()` (tear down + release). They should NOT touch
 * connected/disconnectedCallback — that plumbing is centralized here so a lifecycle
 * fix lands in exactly one spot.
 */
export class B3dChild extends Component {
  owner: B3d | null = null

  /**
   * The scene this child has actually attached to, or `null`.
   *
   * It exists because `sceneReady` was being called MORE THAN ONCE without an
   * intervening `sceneDispose`, and everything a child sets up there — render
   * observers, above all — was quietly accumulating.
   *
   * How: `whenReady` QUEUES when the scene is not up yet, and a child can be
   * connected, disconnected and reconnected before that happens (the doc system
   * does exactly this while mounting a live example, and "moving any ancestor
   * does it to every descendant"). Each connect queues another callback; the
   * disconnect in between disposes nothing, because nothing had been set up
   * yet. Then the scene comes up and every queued callback fires.
   *
   * The symptom is not a crash. It is TIME RUNNING FAST — two observers each
   * subtracting `dt` from the same cooldown, so a launcher with `fireRate: 6`
   * fires eleven times a second. Measured at 2.09× on a doc page, and the
   * launcher was blameless: the duplicates were invisible to it because it only
   * keeps a handle on the LAST observer it added.
   */
  private _attachedScene: BABYLON.Scene | null = null

  connectedCallback() {
    super.connectedCallback()
    /*
    ONE MICROTASK BEFORE WE LOOK AT ANYTHING, because an element can be
    CONNECTED BEFORE ITS ATTRIBUTES HAVE ARRIVED.

    This file used to say that by `connectedCallback` tosijs had drained the
    element's attributes, so they read correctly. Measured, that is not true of
    the HYPHENATED ones. An aircraft built by the doc system logged, at its
    first connect:

      library "vehicles"   mesh-name null

    — the single-word attribute applied, the two-word one not yet. `sceneReady`
    then ran synchronously (the scene was already up, so `whenReady` does not
    defer), read an empty `meshName`, and took neither load branch. No error, no
    warning: an aircraft that silently never loads its model, so the camera
    never transfers and the demo opens inside the terrain.

    It had been surviving on luck. The element is connected a SECOND time later
    — fully configured by then — but `_attachedScene` correctly suppresses a
    repeat, so the save was that the scene itself used to be rebuilt in between,
    which made the guard miss and `sceneReady` run again with real attributes.
    A panel change removed that rebuild and the latent race became a dead demo,
    which is why it looked like a rendering bug in something unrelated.

    A microtask is enough — measured, not assumed: a probe queued from that same
    first connect already saw `mesh-name`. It is also strictly safer than the
    synchronous path for the TDZ hazard documented above, since a `whenReady`
    callback can no longer run inside the caller's own constructor frame.
    Siblings all defer equally and microtasks run FIFO, so relative order is
    unchanged.
    */
    queueMicrotask(() => {
      // Moved-and-removed both land here; only the still-attached ones matter.
      if (!this.isConnected) return
      const owner = findB3dOwner(this)
      if (owner == null) return
      owner.whenReady(() => {
        // A queued callback can outlive the connection that queued it.
        if (!this.isConnected) return
        // ...and several can be queued before the scene exists.
        if (this._attachedScene === owner.scene) return
        this._attachedScene = owner.scene
        this.owner = owner
        this.sceneReady(owner, owner.scene)
      })
    })
  }

  disconnectedCallback() {
    // Only tear down what was actually set up. A disconnect before the scene
    // came up has nothing to dispose, and calling `sceneDispose` anyway is how
    // a subclass ends up guarding every field against a teardown that precedes
    // its own construction.
    if (this._attachedScene != null) {
      this._attachedScene = null
      this.sceneDispose()
    }
    super.disconnectedCallback()
  }

  // Overridden by subclasses. Defaults are no-ops so a bare B3dChild is inert.
  sceneReady(_owner: B3d, _scene: BABYLON.Scene): void {}
  sceneDispose(): void {}
}

/**
 * Build a programmatic XYZ axis gizmo (no asset) for reference/debugging: a medium
 * grey origin ball, and an R/G/B shaft-plus-arrowhead for +X/+Y/+Z. All materials
 * are emissive + unlit ("glow, not lit"), so a scene glow layer makes them bloom.
 * Returned as one `TransformNode` — parent it to any node to pin axes on it, or
 * flip the `axes` attribute on any AbstractMesh geometry (b3dBox/b3dSphere/…).
 */
export function buildAxes(scene: BABYLON.Scene): BABYLON.TransformNode {
  const root = new BABYLON.TransformNode('axes', scene)
  const glow = (name: string, hex: string) => {
    const m = new BABYLON.StandardMaterial(name, scene)
    m.emissiveColor = BABYLON.Color3.FromHexString(hex)
    m.disableLighting = true
    m.diffuseColor = new BABYLON.Color3(0, 0, 0)
    m.specularColor = new BABYLON.Color3(0, 0, 0)
    return m
  }
  const ball = BABYLON.MeshBuilder.CreateSphere(
    'axes-origin',
    { diameter: 0.25, segments: 12 },
    scene
  )
  ball.material = glow('axes-origin-mat', '#808080')
  ball.isPickable = false
  ball.parent = root

  const axes: Array<[string, string, BABYLON.Vector3]> = [
    ['x', '#ff2a2a', new BABYLON.Vector3(1, 0, 0)],
    ['y', '#2aff2a', new BABYLON.Vector3(0, 1, 0)],
    ['z', '#2a6aff', new BABYLON.Vector3(0, 0, 1)],
  ]
  for (const [key, hex, dir] of axes) {
    const mat = glow(`axes-${key}-mat`, hex)
    // Shaft: 0.75 long along the axis, 0.0625 square cross-section, centred at 0.5.
    const shaft = BABYLON.MeshBuilder.CreateBox(
      `axes-${key}-shaft`,
      {
        width: key === 'x' ? 0.75 : 0.0625,
        height: key === 'y' ? 0.75 : 0.0625,
        depth: key === 'z' ? 0.75 : 0.0625,
      },
      scene
    )
    shaft.position = dir.scale(0.5)
    shaft.material = mat
    shaft.isPickable = false
    shaft.parent = root
    // 4-sided cone (arrowhead) at 1.0, pointing along +axis (cone defaults to +Y).
    const cone = BABYLON.MeshBuilder.CreateCylinder(
      `axes-${key}-cone`,
      { diameterTop: 0, diameterBottom: 0.15, height: 0.25, tessellation: 4 },
      scene
    )
    cone.position = dir.scale(1)
    if (key === 'x') cone.rotation.z = -Math.PI / 2
    if (key === 'z') cone.rotation.x = Math.PI / 2
    cone.material = mat
    cone.isPickable = false
    cone.parent = root
  }
  return root
}

export class AbstractMesh extends B3dChild {
  static initAttributes = {
    x: 0,
    y: 0,
    z: 0,
    // ROTATION IS IN DEGREES — `render()` multiplies by DEG_TO_RAD. Stated
    // here because nothing else did, and a plain `ry` reads as radians to
    // anyone who has just been working in Babylon (where it is). The b3d pause
    // demo added radians per second to `ry` for its whole life and turned at
    // roughly one revolution per ten minutes, which read as "the cube doesn't
    // spin" rather than as a unit bug.
    rx: 0,
    ry: 0,
    rz: 0,
    // Show a debug XYZ axis gizmo pinned to this geometry (see buildAxes). The
    // host fades to translucent while on, so the gizmo reads through it.
    axes: false,
  }
  // initAttributes become instance properties at hydration, which TypeScript
  // cannot infer from a static (tosijs 1.10 dropped the `[key: string]: any`
  // that used to hide this, tosijs#36).
  declare x: number
  declare y: number
  declare z: number
  declare rx: number
  declare ry: number
  declare rz: number
  declare axes: boolean

  mesh?: BABYLON.Mesh
  // Generation counter for async asset loads. Bumped on every sceneReady and
  // sceneDispose; loadAssetContainer captures it and discards a callback whose
  // gen no longer matches. Prevents the "frozen clone" bug where a stale load
  // instantiates meshes into the scene after the component has been re-init'd
  // or disposed.
  protected loadGeneration = 0
  private _axesNode?: BABYLON.TransformNode

  get roll() {
    return (this as any).rz
  }
  set roll(v: number) {
    ;(this as any).rz = v
  }
  get pitch() {
    return (this as any).rx
  }
  set pitch(v: number) {
    ;(this as any).rx = v
  }
  get yaw() {
    return (this as any).ry
  }
  set yaw(v: number) {
    ;(this as any).ry = v
  }

  sceneReady(owner: B3d, _scene: BABYLON.Scene) {
    this.owner = owner
  }

  /**
   * Apply a uniform `scale` attribute to the mesh (a non-positive or non-finite
   * value means 1), times a per-axis `stretch` (`"2 1 3"`) if the element has
   * one. Not part of the per-render sync, because most subclasses own
   * `scaling` for their own reasons; elements with a `scale` attribute call
   * this from their `render()` so a later write takes effect.
   */
  protected applyUniformScale(): void {
    const node = this.mesh as unknown as BABYLON.TransformNode | undefined
    if (node?.scaling == null) return
    const s = (this as any).scale
    const u = typeof s === 'number' && Number.isFinite(s) && s > 0 ? s : 1
    /*
    A STRETCH SET BEFORE THE NODE EXISTS survives its arrival (#105, the
    second half): written on the node by hand, it was lost when the library
    instance arrived and the uniform scale went on. As an attribute it is
    applied on adoption like `scale`. Missing or bad axes are 1.
    */
    const st = String((this as any).stretch ?? '')
      .trim()
      .split(/[\s,]+/)
      .map(Number)
    const ax = (i: number) =>
      Number.isFinite(st[i]) && st[i] > 0 ? u * st[i] : u
    const k = `${ax(0)} ${ax(1)} ${ax(2)}`
    /*
    ONLY WHEN `scale` (or the node) CHANGES (tosijs-3d#105). This runs on
    every render, so writing each time wiped any per-axis scale a consumer
    set on the node at the next unrelated attribute write: ensemble's
    stretched pieces lost their stretch the first time they were moved or
    turned. The attribute still wins whenever it is set.
    */
    if (this._scaleApplied?.node === node && this._scaleApplied.k === k) return
    this._scaleApplied = { node, k }
    node.scaling.set(ax(0), ax(1), ax(2))
  }
  private _scaleApplied: { node: BABYLON.TransformNode; k: string } | null =
    null

  private _originShift: ((dx: number, dz: number) => void) | null = null

  /**
   * Opt in to the **floating origin**: on a rebase, shift the `x`/`z`
   * attributes (the source of truth — the per-render sync then moves the
   * node). NOT `registerWorldRoot`, which moves only the node, so the next
   * render would put it back where it was.
   *
   * For world-placed things only. A mesh parented to something else holds
   * LOCAL coordinates and moves with its parent, so it is left alone.
   */
  protected followOrigin(owner: B3d): void {
    if (this._originShift != null) return
    this._originShift = (dx, dz) => {
      if (this.mesh?.parent != null) return
      const attrs = this as any
      attrs.x -= dx
      attrs.z -= dz
      if (this.mesh != null) {
        this.mesh.position.x = attrs.x
        this.mesh.position.z = attrs.z
      }
    }
    owner.addOriginListener(this._originShift)
  }

  sceneDispose() {
    if (this._originShift != null) {
      this.owner?.removeOriginListener(this._originShift)
      this._originShift = null
    }
    // Invalidate any in-flight loadAssetContainer callbacks.
    this.loadGeneration++
    // Dispose the axis gizmo explicitly (it's parented to the mesh, but clear our
    // ref so it rebuilds cleanly on re-init).
    this._axesNode?.dispose()
    this._axesNode = undefined
    if (this.mesh != null) {
      // Takes the mesh's own materials and textures with it — see
      // `disposeMeshTree`. A bare `mesh.dispose()` leaks both on every move.
      disposeMeshTree(this.mesh)
      this.mesh = undefined
    }
    this.owner = null
  }

  /** Attach/detach the debug axis gizmo to track the `axes` attribute. */
  private _updateAxes() {
    const wantAxes = !!(this as any).axes
    if (wantAxes && this.mesh && this._axesNode == null && this.owner) {
      // Fade the host (mesh-level `visibility`, non-destructive) BEFORE parenting
      // the gizmo, so you can see the axes THROUGH the object while the gizmo
      // itself stays fully opaque (it's added after, at visibility 1).
      this._setHostVisibility(0.3)
      this._axesNode = buildAxes(this.owner.scene)
      this._axesNode.parent = this.mesh
    } else if (!wantAxes && this._axesNode != null) {
      this._axesNode.dispose()
      this._axesNode = undefined
      this._setHostVisibility(1) // restore now the gizmo is gone
    }
  }

  private _setHostVisibility(v: number) {
    if (!this.mesh) return
    this.mesh.visibility = v
    for (const child of this.mesh.getChildMeshes()) child.visibility = v
  }

  /**
   * Load a glTF/glb into an AssetContainer, with race-safe gen tracking.
   * The onLoaded callback is only invoked if the component hasn't been
   * disposed or had a newer load supersede it. Subclasses use this from
   * their sceneReady to safely resolve async asset loads.
   */
  protected loadAssetContainer(
    scene: BABYLON.Scene,
    url: string,
    onLoaded: (container: BABYLON.AssetContainer) => void
  ): void {
    const gen = ++this.loadGeneration
    BABYLON.SceneLoader.LoadAssetContainer(
      url,
      undefined,
      scene,
      (container) => {
        if (gen !== this.loadGeneration) return
        onLoaded(container)
      }
    )
  }

  render() {
    super.render()
    if (this.mesh?.position) {
      const { x, y, z } = this as any
      this.mesh.position.x = x
      this.mesh.position.y = y
      this.mesh.position.z = z
      this.mesh.rotationQuaternion = BABYLON.Quaternion.RotationYawPitchRoll(
        this.yaw * DEG_TO_RAD,
        this.pitch * DEG_TO_RAD,
        this.roll * DEG_TO_RAD
      )
    }
    this._updateAxes()
  }
}

/**
 * Is this camera actually listening to the canvas right now?
 *
 * Ground truth from Babylon's own input manager rather than a flag we keep,
 * because several components attach or detach a camera directly instead of
 * going through `setActiveCamera` (b3d-galaxy, b3d-svg-plane, the XR restore)
 * — a remembered flag goes stale behind them.
 *
 * Written defensively (optional chaining all the way down) because it is used
 * on the RESUME path: guessing "attached" wrongly hands the canvas to a camera
 * that was deliberately never given it, and guessing "not attached" wrongly
 * only costs the user a click.
 */
export const cameraIsAttached = (
  cam: { inputs?: { attachedToElement?: boolean } } | null | undefined
): boolean => cam?.inputs?.attachedToElement === true

const warnedFetch = new Set<string>()
/**
 * A FETCHED attribute's value, or '' if it is not a string (tosijs-3d#95,
 * from ensemble). `String(value)` turned a JSON array like
 * `["http://evil…"]` into the URL inside it and fetched that, past any
 * consumer's URL rule that only looked at strings. Anything that is not a
 * string is REFUSED (once-per-field warning), never coerced.
 *
 * This is not a URL policy: the element still fetches whatever string it is
 * given, and validating that is the embedder's job (see the `uri-reference`
 * fields in `sceneSchemas`).
 */
export function fetchedUrl(value: unknown, where: string): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string') return value
  if (!warnedFetch.has(where)) {
    warnedFetch.add(where)
    console.warn(
      `${where}: refusing a non-string URL (${typeof value}); a fetched attribute must be a string.`
    )
  }
  return ''
}

/**
 * Make a node refuse a parent that is itself or one of its own descendants,
 * and say once who asked (console, and the debug sink).
 *
 * Babylon accepts such a parent. The node is then its own ancestor, and the
 * next `setEnabled` or `dispose` that reaches it recurses until the stack runs
 * out, which stops the render loop.
 *
 * Use it on any node an XR camera is parented to. Babylon's hand tracking
 * (9.16) does this to that node: `WebXRHand.setHandMesh` takes the TOPMOST
 * ancestor of the hand mesh as its root, and every frame parents that root to
 * `xrCamera.parent`. The first time hands appear the root is the hand model's
 * own; that parents it beneath the camera's parent. The hand meshes are shared
 * and reused, so the second time hands appear (put a controller down, pick it
 * up, put it down again) the walk goes one step further and the root IS the
 * camera's parent, which is then parented to itself. Found on a Quest (#3135).
 */
const refusing = new WeakSet<BABYLON.Node>()
export function refuseOwnAncestor(node: BABYLON.Node): void {
  if (refusing.has(node)) return
  refusing.add(node)
  let proto: object | null = Object.getPrototypeOf(node)
  let slot: PropertyDescriptor | undefined
  while (proto != null && slot == null) {
    slot = Object.getOwnPropertyDescriptor(proto, 'parent')
    proto = Object.getPrototypeOf(proto)
  }
  if (slot?.get == null || slot.set == null) return
  const { get, set } = slot
  let told = false
  Object.defineProperty(node, 'parent', {
    configurable: true,
    get() {
      return get.call(this)
    },
    set(next: BABYLON.Node | null) {
      let n: BABYLON.Node | null = next
      for (let i = 0; n != null && i < 256; i++) {
        if (n === node) {
          // Once: the hand-tracking case asks again every frame.
          if (!told) {
            told = true
            const stack = String(new Error('refused').stack ?? '').slice(
              0,
              4000
            )
            console.warn(
              `tosijs-3d: refused to parent "${node.name}" beneath itself (to "${next?.name}")`,
              stack
            )
            sendDebug('parent-refused', {
              node: node.name,
              to: `${next?.name} [${next?.getClassName()}]`,
              stack,
            })
          }
          return
        }
        n = n.parent
      }
      set.call(this, next)
    },
  })
}

/**
 * `refuseOwnAncestor` on a node and on everything above it. For the XR rig
 * whenever it is parented to something: hand tracking parents the TOPMOST
 * ancestor of the hand mesh beneath the rig, and with the rig riding a
 * vehicle that ancestor is the vehicle (or its chase anchor), not the rig.
 * Guarding only the rig let the aircraft be parented beneath it, and the loop
 * hung a Quest mid-flight.
 */
export function refuseOwnAncestors(node: BABYLON.Node): void {
  for (let n: BABYLON.Node | null = node; n != null; n = n.parent)
    refuseOwnAncestor(n)
}

/**
 * A node's world matrix as of now, its ancestors first. `computeWorldMatrix`
 * reads the parent's CACHED matrix, which is last frame's when the parent has
 * moved since and has not been drawn yet.
 */
export function freshWorldMatrix(node: BABYLON.Node): BABYLON.Matrix {
  const chain: BABYLON.Node[] = []
  for (let n: BABYLON.Node | null = node; n != null; n = n.parent) chain.push(n)
  for (let i = chain.length - 1; i >= 0; i--)
    (chain[i] as BABYLON.TransformNode).computeWorldMatrix?.(true)
  return node.getWorldMatrix()
}
